/**
 * A table-style overlay to inspect and sort node relationships with MOSCOW filters.
 */
import { ChangeEvent, useState } from 'react';
import { useStore } from 'store/useStore';
import Select from 'components/ui/Select';
import { IconButton } from 'components/ui';
import { OstNode } from 'types';

type TableRow = {
    opportunity: OstNode | null;
    solution: OstNode | null;
    item: OstNode | null;
};

const MOSCOW_RANK = {
    'Must have': 0,
    'Should have': 1,
    'Could have': 2,
    "Won't have (MVP)": 3,
    '': 4,
    undefined: 4,
    null: 4,
};

/**
 * Builds combined opportunity-solution-story rows for table display.
 */
function buildRows(opportunities: OstNode[], solutions: OstNode[], userStories: OstNode[]): TableRow[] {
    const opportunityMap = Object.fromEntries(opportunities.map((o) => [o.id, o]));
    const rows: TableRow[] = [];

    solutions.forEach((sol) => {
        const op = sol.parentId ? opportunityMap[sol.parentId] : null;
        const children = userStories.filter((u) => u.parentId === sol.id);
        if (children.length === 0) {
            rows.push({ opportunity: op, solution: sol, item: null });
        } else {
            children.forEach((child) => rows.push({ opportunity: op, solution: sol, item: child }));
        }
    });

    opportunities.forEach((op) => {
        const hasSolution = solutions.some((sol) => sol.parentId === op.id);
        if (!hasSolution) rows.push({ opportunity: op, solution: null, item: null });
    });

    return rows;
}

/**
 * Sort table rows by chosen MOSCOW ordering and fallback title.
 */
function formatMoscowLabel(value?: string | null): string {
    if (!value) return '';
    switch (value) {
        case 'Must have':
            return 'Must';
        case 'Should have':
            return 'Should';
        case 'Could have':
            return 'Could';
        case "Won't have (MVP)":
            return "Won't (MVP)";
        case 'Would have':
            return 'Would';
        default:
            return value.replace(/have/gi, '').trim();
    }
}

const SOFT_MOSCOW_COLORS: Record<string, string> = {
    'Must have': '#FCA5A5',
    'Should have': '#FCD34D',
    'Could have': '#FDE68A',
    "Won't have (MVP)": '#93C5FD',
    'Would have': '#93C5FD',
};

function getMoscowColor(value?: string | null): string {
    if (!value) return 'transparent';
    return SOFT_MOSCOW_COLORS[value] || 'transparent';
}

const TYPE_LABELS: Record<string, string> = { story: 'story', experiment: 'experiment', enablerNode: 'enabler' };

const STATUS_OPTS_SOLUTION = ['Idea', 'Validated', 'In progress', 'Done'];
const STATUS_OPTS_STORY = ['Draft', 'Ready to build', 'Pushed to GitHub', 'Done', 'Idea', 'Validated', 'In progress'];

function sortRows(rows: TableRow[], sortBy: 'solution' | 'story'): TableRow[] {
    const getRank = (val: string | undefined | null) => MOSCOW_RANK[val as keyof typeof MOSCOW_RANK] ?? 4;

    return [...rows].sort((a, b) => {
        if (sortBy === 'story') {
            const rankDiff = getRank(a.item?.moscow) - getRank(b.item?.moscow);
            if (rankDiff !== 0) return rankDiff;
            return (a.solution?.title || '').localeCompare(b.solution?.title || '');
        }
        const rankDiff = getRank(a.solution?.moscow) - getRank(b.solution?.moscow);
        if (rankDiff !== 0) return rankDiff;
        return (a.item?.title || '').localeCompare(b.item?.title || '');
    });
}

type TableViewProps = {
    onClose: () => void;
};

export default function TableView({ onClose }: TableViewProps) {
    const { opportunities, solutions, userStories, updateNode } = useStore();
    const [sortBy, setSortBy] = useState<'solution' | 'story'>('story');
    const [expandedColumn, setExpandedColumn] = useState<'opportunity' | 'solution' | 'story' | null>(null);

    const rows = buildRows(opportunities, solutions, userStories);
    const sortedRows = sortRows(rows, sortBy);

    const getNodeColWidth = (col: 'opportunity' | 'solution' | 'story') => {
        if (!expandedColumn) return '32%';
        if (expandedColumn === col) return '45%';
        return '20%';
    };

    const onHeaderClick = (col: 'opportunity' | 'solution' | 'story') => {
        setExpandedColumn((current) => (current === col ? null : col));
    };

    return (
        <div className='modal-overlay'>
            <div className='tableview-panel'>
                <div className='tableview-header'>
                    <span className='tableview-title'>Priorities Board</span>
                    <div className='tableview-controls'>
                        <label className='tableview-label'>Sort by:</label>
                        <select
                            value={sortBy}
                            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                setSortBy(e.target.value as 'solution' | 'story')
                            }
                            className='form-select tableview-select'
                        >
                            <option value='solution'>Solution MoSCoW</option>
                            <option value='story'>Story/Experiment MoSCoW</option>
                        </select>
                        <IconButton onClick={onClose}>✕</IconButton>
                    </div>
                </div>
                <div className='tableview-body'>
                    <div className='tableview-scroll'>
                        <table className='tableview-table'>
                            <colgroup>
                                <col style={{ '--col-width': getNodeColWidth('opportunity') } as React.CSSProperties} className="tableview-col" />
                                <col style={{ '--col-width': getNodeColWidth('solution') } as React.CSSProperties} className="tableview-col" />
                                <col style={{ '--col-width': getNodeColWidth('story') } as React.CSSProperties} className="tableview-col" />
                                <col className="tableview-col--fixed-12" />
                                <col className="tableview-col--fixed-12" />
                                <col className="tableview-col--fixed-12" />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className='tableview-th' onClick={() => onHeaderClick('opportunity')}>
                                        Opportunity
                                    </th>
                                    <th className='tableview-th' onClick={() => onHeaderClick('solution')}>
                                        Solution
                                    </th>
                                    <th className='tableview-th' onClick={() => onHeaderClick('story')}>
                                        Story / Experiment
                                    </th>
                                    <th className='tableview-th small-col'>Type</th>
                                    <th className='tableview-th small-col'>Status</th>
                                    <th className='tableview-th small-col'>MOSCOW</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedRows.map((row, index) => {
                                    const status = row.item?.status || row.solution?.status || row.opportunity?.status || '';
                                    return (
                                        <tr key={`${row.solution?.id || ''}-${row.item?.id || ''}-${index}`}>
                                            <td className='tableview-td node-col'>{row.opportunity?.title || ''}</td>
                                            <td className='tableview-td node-col'>{row.solution?.title || ''}</td>
                                            <td className='tableview-td node-col'>{row.item?.title || ''}</td>
                                            <td className='tableview-td small-col'>{TYPE_LABELS[row.item?.type || ''] || row.item?.type || ''}</td>
                                            <td className='tableview-td small-col'>
                                                {row.item ? (
                                                    <Select
                                                        opts={STATUS_OPTS_STORY}
                                                        value={row.item.status}
                                                        onChange={(v: string) => updateNode(row.item!.type, row.item!.id, { status: v })}
                                                        className='tableview-status-select'
                                                    />
                                                ) : row.solution ? (
                                                    <Select
                                                        opts={STATUS_OPTS_SOLUTION}
                                                        value={row.solution.status}
                                                        onChange={(v: string) => updateNode('solution', row.solution!.id, { status: v })}
                                                        className='tableview-status-select'
                                                    />
                                                ) : (
                                                    status
                                                )}
                                            </td>
                                            <td
                                                className='tableview-td small-col moscow-col'
                                                style={{
                                                    '--row-color': getMoscowColor(row.item?.moscow || row.solution?.moscow),
                                                } as React.CSSProperties}
                                            >
                                                {formatMoscowLabel(row.item?.moscow || row.solution?.moscow)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
